import { useState } from 'react';
import { groupsApi } from '../../../services/groups-api-client';
import { mutationErrorMessage } from '../../../lib/mutation-error';
import { groupToPayload } from '../../../lib/groupPayload';
import { normalizeTripDate } from '../../../lib/reminderPlan';
import {
  draftAfterStartEdit,
  draftFromGroup,
  plansOfferingReschedule,
  canSaveTripDates,
  startShiftDays,
  tripDatesDirty,
  tripDatesWarning,
  validateTripDates,
  type TripDatesDraft,
} from '../../../lib/tripDates';
import { useDateShiftSession, type Session } from './useDateShiftSession';
import type { GroupDetail } from '../../../types/groups';

export { SET_ZONE_FIRST, type MoveResult, type TripDatesOffer } from './useDateShiftSession';

/**
 * The Trip dates card's state: the two dates being edited and the save. What follows a save (the offer to move the schedule,
 * the retry, the way back in, the one guest notice) is the move session, in useDateShiftSession.
 *
 *  - `save`: read the group fresh, save the dates through groupToPayload, and, when the START moved, hand over to the move session.
 *    A published trip is then offered ONE guest notice, whatever else happened.
 */
export function useTripDates(group: GroupDetail, onSaved: () => void) {
  const moves = useDateShiftSession(group, onSaved);
  const saved = draftFromGroup(group);
  const [draft, setDraft] = useState<TripDatesDraft>(saved);
  const [endTouched, setEndTouched] = useState(false);
  const [endFollowed, setEndFollowed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [moved, setMoved] = useState<{ from: string; to: string } | null>(null);
  // The question the host said yes to ("Use these dates anyway"); a different question, after more editing, asks again.
  const [confirmed, setConfirmed] = useState<string | null>(null);

  const dirty = tripDatesDirty(draft, saved);
  // Only complain about what the host has touched: a saved trip with one date is not an error until they edit it.
  const errors = dirty ? validateTripDates(draft, saved) : {};
  const dateQuestion = dirty ? tripDatesWarning(draft, saved) : null;
  const canSave = canSaveTripDates(draft, saved, confirmed);

  const setStart = (value: string) => {
    // From the SAVED dates, not the previous draft: typing a year emits 0002, 0020, 0202, 2027 on the way.
    const next = draftAfterStartEdit(saved, draft, value, endTouched);
    setDraft(next.draft);
    setEndFollowed(next.endFollowed);
  };
  const setEnd = (value: string) => {
    setDraft((prev) => ({ ...prev, end: value }));
    setEndTouched(true);
    setEndFollowed(false);
  };
  const resetDraft = () => {
    setDraft(saved);
    setEndTouched(false);
    setEndFollowed(false);
    setConfirmed(null);
  };

  const save = async () => {
    setSaveError('');
    moves.clearMessages();
    moves.clearSummary();
    setMoved(null);
    setSaving(true);
    try {
      // The on-screen group may be stale; updateGroup REPLACES the record, so build the body from a fresh read.
      let fresh: GroupDetail;
      try {
        fresh = await groupsApi.getGroup(group.id);
      } catch (e) {
        setSaveError(`Could not re-check the trip before saving, so nothing was changed. ${mutationErrorMessage(e)}`.trim());
        return;
      }
      const oldStart = normalizeTripDate(fresh.dateStart);
      const oldEnd = normalizeTripDate(fresh.dateEnd);
      try {
        await groupsApi.updateGroup(group.id, groupToPayload(fresh, { dateStart: draft.start, dateEnd: draft.end }));
      } catch (e) {
        setSaveError(mutationErrorMessage(e) || 'Could not save the trip dates.');
        return;
      }
      onSaved();
      setEndTouched(false);
      setEndFollowed(false);
      setConfirmed(null);
      const days = oldStart ? startShiftDays(oldStart, draft.start) : null;
      const session: Session = {
        before: { start: oldStart, end: oldEnd },
        after: { start: draft.start, end: draft.end },
        days: days || null,
        datesChanged: true,
        published: Boolean(fresh.published),
        movedEvents: [],
        movedDinners: [],
      };
      if (oldStart && days) {
        // Only mention "Reschedule plan" when a plan is there to offer it; if that cannot be read, say nothing rather than promise it.
        try {
          const rows = await groupsApi.listScheduledBroadcasts(group.id);
          if (plansOfferingReschedule(rows, draft.start) > 0) setMoved({ from: oldStart, to: draft.start });
        } catch {
          // nothing to say
        }
        if (await moves.begin(fresh, session, oldStart, draft.start, days)) return;
      }
      moves.offerNotice(session);
    } finally {
      setSaving(false);
    }
  };

  return {
    saved, draft, setStart, setEnd, resetDraft, endFollowed, busy: saving || moves.busy, error: saveError || moves.error,
    warning: moves.warning, moved, dirty, errors, save, dateQuestion, confirmed, setConfirmed, canSave,
    offer: moves.offer, plan: moves.plan, moveError: moves.moveError, result: moves.result, summary: moves.summary,
    notice: moves.notice, setNotice: moves.setNotice, moveSelected: moves.moveSelected, retryFailed: moves.retryFailed,
    openCatchUp: moves.openCatchUp, setOfferDays: moves.setOfferDays, finish: moves.finish, outside: moves.outside,
    zoneMissing: moves.zoneMissing,
  };
}
